import cn from "classnames";
import type { Ref } from "react";

import { ADTLogo } from "@/components/icons/adtLogo.icon";
import { getFrontendIconPreview } from "@/components/icons/iconRegistry";
import { PhoneIcon } from "@/components/icons/phone.icon";
import { Link } from "@/components/link/link.component";
import { PhoneNumber } from "@/components/phoneNumber/phoneNumber.component";

import { resolveLinkHref } from "@/utils/url.util";

import { brown } from "@/app/font";

import type { FreeQuoteDrawerDefaults } from "./freeQuoteDrawer.defaults";
import { FreeQuoteSuccessDark } from "./freeQuoteSuccessDark.component";

export const FreeQuoteSuccess = ({
  content,
  headingRef,
  headingId = "free-quote-drawer-title",
  theme = "standard",
  firstName,
  installPref,
}: FreeQuoteSuccessProps) => {
  const shopHref = resolveLinkHref(content.shopCta.link);
  const shopOpenInNewTab = Boolean(content.shopCta.link.openInNewTab);
  const greeting = firstName ? `Hi, ${firstName}!` : content.successHeading;

  if (theme === "darkBlue") {
    return (
      <FreeQuoteSuccessDark
        content={content}
        headingRef={headingRef}
        firstName={firstName}
        installPref={installPref}
      />
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col pb-10 text-center">
      <div className="px-7 pt-20 sm:px-14 lg:px-16">
        <ADTLogo
          width={56}
          height={56}
          className="mx-auto"
          aria-hidden="true"
        />
        <h2
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className={cn(
            brown.className,
            "text-greyscale-headline mt-6 text-[32px] leading-tight font-bold outline-none sm:text-[40px]",
          )}>
          {greeting}
        </h2>
        <p
          className={cn(
            brown.className,
            "text-secondary mt-2 text-[22px] leading-snug font-bold sm:text-[26px]",
          )}>
          {content.successMessage}
        </p>
      </div>

      <div className="bg-greyscale-background mt-8 px-7 py-8 sm:px-14 lg:px-16">
        <p className="text-greyscale-headline text-base text-[24px] font-bold">
          {content.nextStepsHeading}
        </p>
        <ul className="mt-6 flex flex-col gap-7">
          {content.nextSteps.map((step) => {
            const Icon = getFrontendIconPreview(step.icon);
            return (
              <li key={step.title} className="flex flex-col items-center">
                {Icon ? (
                  <Icon
                    width={24}
                    height={24}
                    className="text-secondary"
                    style={{ strokeWidth: "1.8px" }}
                    aria-hidden="true"
                  />
                ) : null}
                <p
                  className={cn(
                    brown.className,
                    "text-secondary mt-3 text-lg font-bold",
                  )}>
                  {step.title}
                </p>
                <p className="text-greyscale-body mt-1 max-w-[28rem] text-[15px] leading-snug">
                  {step.description}
                </p>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="mt-8 flex flex-col items-center px-5">
        <p
          className={cn(
            brown.className,
            "text-secondary text-[22px] leading-snug font-bold sm:text-[24px]",
          )}>
          {content.skipWaitHeading}
        </p>
        <p className="text-greyscale-body mt-3 text-[16px] leading-snug">
          {content.skipWaitMessageCompact}
        </p>
        <PhoneNumber
          showNumber
          formatNumber
          className={cn(
            brown.className,
            "button border-secondary text-secondary disabled:border-greyscale-strength disabled:text-greyscale-strength focus-visible:ring-secondary mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-2 px-6 py-2.5 font-bold uppercase duration-300 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:bg-transparent disabled:select-none md:text-[18px]",
          )}
          desktopClassName="lg:flex"
          mobileClassName="flex">
          <PhoneIcon width={20} height={20} aria-hidden="true" title="" />
        </PhoneNumber>

        {installPref === "diy" || installPref === "difm_diy" ? (
          <Link
            href={shopHref}
            secondary
            target={shopOpenInNewTab ? "_blank" : undefined}
            rel={shopOpenInNewTab ? "noopener noreferrer" : undefined}
            className="mt-4 w-full rounded-full bg-[#00226A]! px-8 py-3.5 text-white hover:bg-[#07408b]! lg:mt-6">
            <span className="lg:inline">{content.shopCta.compactLabel}</span>
          </Link>
        ) : null}
      </div>
    </div>
  );
};

type FreeQuoteSuccessProps = {
  content: FreeQuoteDrawerDefaults;
  headingRef?: Ref<HTMLHeadingElement>;
  headingId?: string;
  theme?: "standard" | "darkBlue";
  firstName?: string;
  installPref?: string;
};
